import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

import { addMonths, startOfDay } from "../functions/date-utils";

/**
 * F4: error keys emitted by {@link CustomValidators.dateRangeValidator} on the FormGroup.
 * Mirrors legacy validateDateCriteria / onChangeStartDate / onChangeEndDate
 * (e.g. PaymentEnquiryComposer.java:231-250, SubmissionEnquiryComposer.java:190-216).
 */
export const DATE_RANGE_FROM_AFTER_TO = "dateFromAfterTo";
export const DATE_RANGE_EXCEEDS_MAX = "dateRangeExceedsMaxMonths";

/**
 * F4: maps a date-range validation error to its legacy-equivalent message.
 * Per-screen forms call this to surface the message the way other sat forms do.
 */
export function dateRangeErrorMessage(
  errors: ValidationErrors | null | undefined,
): string | null {
  if (!errors) {
    return null;
  }
  if (errors[DATE_RANGE_FROM_AFTER_TO]) {
    return "Date From cannot be later than Date To";
  }
  const exceeds = errors[DATE_RANGE_EXCEEDS_MAX];
  if (exceeds) {
    return `Date From cannot be more than ${exceeds.maxSpanMonths} month earlier than Date To`;
  }
  return null;
}

export class CustomValidators {
  public static patternValidator(
    regex: RegExp,
    error: ValidationErrors,
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        // if control is empty return no error
        return null;
      }

      // test the value of the control against the regexp supplied
      const valid = regex.test(control.value);

      // if true, return no error (no error), else return error passed in the second parameter
      return valid ? null : error;
    };
  }

  /**
   * F4: cross-field date-range validator (attach to the FormGroup).
   * Rule (legacy parity): Date From must not be later than Date To, and the range must not
   * span more than `maxSpanMonths` calendar months (legacy RPT_FILTER_PERIOD, default 1).
   * Only fires once BOTH dates are present (mirrors the legacy `!= null` guards).
   * Emits a group-level error ({@link DATE_RANGE_FROM_AFTER_TO} / {@link DATE_RANGE_EXCEEDS_MAX});
   * use {@link dateRangeErrorMessage} to render it.
   */
  public static dateRangeValidator(
    fromKey: string,
    toKey: string,
    maxSpanMonths = 1,
  ): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const fromValue = group.get(fromKey)?.value;
      const toValue = group.get(toKey)?.value;
      if (!fromValue || !toValue) {
        return null;
      }

      const from = startOfDay(fromValue);
      const to = startOfDay(toValue);
      if (!from || !to) {
        return null;
      }

      if (from.getTime() > to.getTime()) {
        return { [DATE_RANGE_FROM_AFTER_TO]: true };
      }
      if (to.getTime() > addMonths(from, maxSpanMonths).getTime()) {
        return { [DATE_RANGE_EXCEEDS_MAX]: { maxSpanMonths } };
      }
      return null;
    };
  }

  public static passwordMatchValidator(control: AbstractControl) {
    const password: string = control.get("password").value; // get password from our password form control
    const confirmPassword: string = control.get("confirmPassword").value; // get password from our confirmPassword form control
    // compare is the password math
    if (password !== confirmPassword) {
      // if they don't match, set an error in our confirmPassword form control
      control.get("confirmPassword").setErrors({ NoPassswordMatch: true });
    }
  }
}
